import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Mail, Loader2, User as UserIcon, LogIn, ArrowLeft } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import PasswordInput from '../components/PasswordInput';
import { toast } from 'sonner';
import axios from 'axios';
import SEO from '../components/SEO';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function formatApiErrorDetail(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

const LoginPage = () => {
  const { user, loading, login, loginWithPassword, registerWithPassword } = useAuth();
  const [mode, setMode] = useState('login'); // 'login' | 'register' | 'forgot'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [forgotSent, setForgotSent] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const redirect = new URLSearchParams(location.search).get('redirect') || '/';

  useEffect(() => {
    if (!loading && user) navigate(redirect, { replace: true });
  }, [user, loading, navigate, redirect]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      if (mode === 'login') {
        await loginWithPassword({ email, password });
        toast.success('Signed in');
        navigate(redirect, { replace: true });
      } else if (mode === 'register') {
        await registerWithPassword({ email, password, name });
        toast.success('Account created');
        navigate(redirect, { replace: true });
      } else if (mode === 'forgot') {
        await axios.post(`${API}/auth/forgot-password`, { email });
        setForgotSent(true);
        toast.success('Check your inbox for the reset link');
      }
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message || 'Auth failed';
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const switchMode = (m) => { setMode(m); setError(''); setForgotSent(false); };

  return (
    <>
      <SEO title={mode === 'login' ? 'Sign in' : mode === 'register' ? 'Create account' : 'Reset password'} description="Sign in to PDF Studio Pro" />
      <div className="min-h-[calc(100vh-3.5rem)] flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-md" data-testid="login-page">
          <div className="bg-card border border-border rounded-2xl shadow-xl p-6 sm:p-8">
            <div className="flex flex-col items-center text-center mb-6">
              <div className="w-14 h-14 rounded-2xl bg-primary flex items-center justify-center mb-3">
                <span className="text-white font-bold text-lg">PS</span>
              </div>
              <h1 className="text-2xl font-bold font-heading tracking-tight">
                {mode === 'login' ? 'Welcome back' : mode === 'register' ? 'Create your account' : 'Forgot your password?'}
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                {mode === 'login' ? 'Sign in to edit and download PDFs'
                  : mode === 'register' ? 'Free tier: 1 download per day'
                  : 'Enter your email and we\u2019ll send you a reset link'}
              </p>
            </div>

            {mode !== 'forgot' && (
              <>
                <Button
                  type="button"
                  data-testid="google-login-btn"
                  onClick={login}
                  variant="outline"
                  className="w-full h-11 rounded-lg"
                >
                  <svg width="18" height="18" viewBox="0 0 48 48" className="mr-2" aria-hidden="true">
                    <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.5l6.7-6.7C35.6 2.4 30.2 0 24 0 14.6 0 6.5 5.4 2.6 13.2l7.8 6.1C12.3 13.2 17.7 9.5 24 9.5z"/>
                    <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.9 7.2l7.6 5.9c4.4-4.1 7.1-10.2 7.1-17.6z"/>
                    <path fill="#FBBC05" d="M10.4 28.6c-.6-1.7-.9-3.5-.9-5.6s.3-3.9.9-5.6l-7.8-6.1C.8 15.1 0 19.4 0 24s.8 8.9 2.6 12.7l7.8-6.1z"/>
                    <path fill="#34A853" d="M24 48c6.5 0 12-2.1 15.9-5.8l-7.6-5.9c-2.1 1.4-4.8 2.2-8.3 2.2-6.3 0-11.7-3.7-13.6-9.1l-7.8 6.1C6.5 42.6 14.6 48 24 48z"/>
                  </svg>
                  Continue with Google
                </Button>

                <div className="flex items-center gap-3 my-5">
                  <div className="flex-1 h-px bg-border" />
                  <span className="text-xs uppercase tracking-wider text-muted-foreground">or</span>
                  <div className="flex-1 h-px bg-border" />
                </div>
              </>
            )}

            {mode === 'forgot' && forgotSent ? (
              <div className="text-center py-2" data-testid="forgot-sent">
                <p className="text-sm text-foreground mb-2">If an account with that email exists, we&apos;ve sent a reset link.</p>
                <p className="text-xs text-muted-foreground mb-4">Check your inbox (and spam folder). The link expires in 60 minutes.</p>
                <Button variant="outline" className="w-full" onClick={() => switchMode('login')} data-testid="back-to-login-btn">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back to sign in
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-3" data-testid="password-form">
                {mode === 'register' && (
                  <div className="relative">
                    <UserIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                      type="text"
                      placeholder="Your name (optional)"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="w-full h-11 pl-9 pr-3 rounded-lg bg-background border border-border focus:border-primary focus:outline-none text-sm"
                      autoComplete="name"
                      data-testid="input-name"
                    />
                  </div>
                )}
                <div className="relative">
                  <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="email"
                    required
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full h-11 pl-9 pr-3 rounded-lg bg-background border border-border focus:border-primary focus:outline-none text-sm"
                    autoComplete="email"
                    data-testid="input-email"
                  />
                </div>
                {mode !== 'forgot' && (
                  <PasswordInput
                    required
                    minLength={mode === 'register' ? 6 : 1}
                    placeholder={mode === 'register' ? 'At least 6 characters' : 'Your password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                    data-testid="input-password"
                  />
                )}

                {mode === 'login' && (
                  <div className="text-right">
                    <button
                      type="button"
                      onClick={() => switchMode('forgot')}
                      className="text-xs text-muted-foreground hover:text-primary hover:underline"
                      data-testid="forgot-password-link"
                    >
                      Forgot password?
                    </button>
                  </div>
                )}

                {error && (
                  <p className="text-sm text-destructive text-center" data-testid="auth-error">{error}</p>
                )}

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full h-11 rounded-lg"
                  data-testid={mode === 'login' ? 'submit-login-btn' : mode === 'register' ? 'submit-register-btn' : 'submit-forgot-btn'}
                >
                  {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : mode === 'forgot' ? <Mail className="w-4 h-4 mr-2" /> : <LogIn className="w-4 h-4 mr-2" />}
                  {mode === 'login' ? 'Sign in' : mode === 'register' ? 'Create account' : 'Send reset link'}
                </Button>
              </form>
            )}

            <p className="mt-5 text-center text-sm text-muted-foreground">
              {mode === 'login' && (
                <>
                  New here?{' '}
                  <button type="button" onClick={() => switchMode('register')} className="text-primary font-medium hover:underline" data-testid="switch-to-register">
                    Create an account
                  </button>
                </>
              )}
              {mode === 'register' && (
                <>
                  Already have an account?{' '}
                  <button type="button" onClick={() => switchMode('login')} className="text-primary font-medium hover:underline" data-testid="switch-to-login">
                    Sign in
                  </button>
                </>
              )}
              {mode === 'forgot' && !forgotSent && (
                <button type="button" onClick={() => switchMode('login')} className="text-primary font-medium hover:underline" data-testid="switch-to-login-from-forgot">
                  <ArrowLeft className="w-3.5 h-3.5 mr-1 inline-block align-[-2px]" />
                  Back to sign in
                </button>
              )}
            </p>
          </div>

          <p className="mt-4 text-center text-xs text-muted-foreground">
            <Link to="/" className="hover:underline">Back to home</Link>
          </p>
        </div>
      </div>
    </>
  );
};

export default LoginPage;
